import React from 'react';

import { Container, Row, Col } from 'react-bootstrap';

import { translateComponent } from '@/utils/tolgee/translateComponent';
import { LomavisTitle1 } from '@/lomavisSharedUi/molecules/typography/titles/LomavisTitle1/LomavisTitle1';
import {
  LomavisChart1Data,
  LomavisDoughnutChartAndTable1
} from '@/lomavisSharedUi/organisms/cards/analytics/LomavisDoughnutChartAndTable1/LomavisDoughnutChartAndTable1';
import { LomavisDemographicsFollowerCities } from '@/lomavisSharedUi/organisms/cards/analytics/LomavisDemographicsFollowerCities/LomavisDemographicsFollowerCities';

import { BarChartData } from '@/lomavisSharedUi/atoms/charts/LomavisBarChart2/LomavisBarChart2';
import { ILomavisMapWithHighlights1Data } from '@/lomavisSharedUi/atoms/charts/LomavisMapWithHighlights1/LomavisMapWithHighlights1';
import { ALL } from '@/config/constants';

interface LomavisDemographicsSectionForPlatform1Props {
  /** The title of the section */
  title: string;
  /** The platform identifier (e.g., 'facebook', 'google') */
  platform: string;
  /** Data for languages chart */
  languageData?: LomavisChart1Data[] | null;
  /** Data for age chart */
  ageData?: LomavisChart1Data[] | null;
  /** Data for gender chart */
  genderData?: LomavisChart1Data[] | null;
  /** Data for seniority chart */
  seniorityData?: LomavisChart1Data[] | null;
  /** Data for industries chart */
  industriesData?: LomavisChart1Data[] | null;
  /** Data for functions chart */
  functionsData?: LomavisChart1Data[] | null;
  /** Data for company sizes chart */
  companySizesData?: LomavisChart1Data[] | null;
  /** Data for cities */
  citiesData?: {
    barChartData: BarChartData[];
    mapData: ILomavisMapWithHighlights1Data[];
  } | null;

  hideTitle?: boolean; // Optional prop to hide the title
  viewMoreButtonUrl?: string; // Optional URL for view more button
  showViewMoreButton?: boolean;
  activeSupportedPlatforms?: string[];
}

/**
 * Templates: LomavisDemographicsSectionForPlatform1
 * A section that displays demographic information for a platform
 */
export const LomavisDemographicsSectionForPlatform1: React.FC<
  LomavisDemographicsSectionForPlatform1Props
> = ({
  title,
  platform = '',
  activeSupportedPlatforms = [],
  languageData = [],
  ageData = [],
  genderData = [],
  seniorityData = [],
  industriesData = [],
  functionsData = [],
  companySizesData = [],
  citiesData = { barChartData: [], mapData: [] },
  hideTitle = false,
  viewMoreButtonUrl = '#',
  showViewMoreButton = false
}) => {
  // Create a safe translation function that falls back to default values if translations fail
  const t = translateComponent('LomavisDemographicsSectionForPlatform1');

  return (
    <Container className="mb-5">
      {/* Platform Title with Icon */}
      {!hideTitle && (
        <Row className="mb-4">
          <Col>
            <LomavisTitle1
              text={t('title', { platform })}
              platform={platform}
            />
          </Col>
        </Row>
      )}

      {/* Cities */}
      {citiesData && (
        <Row className="mb-4">
          <Col className={'px-0'}>
            <LomavisDemographicsFollowerCities
              title={t('cities')}
              barChartData={citiesData.barChartData}
              mapData={citiesData.mapData}
              showDownloadButton={platform !== ALL}
              showViewMoreButton={showViewMoreButton}
              viewMoreButtonUrl={viewMoreButtonUrl}
              platforms={
                platform !== ALL ? [platform] : activeSupportedPlatforms
              }
            />
          </Col>
        </Row>
      )}

      {/* Demographics Charts */}
      {languageData && (
        <Row className="mb-4">
          <Col className="mb-4 px-0">
            <LomavisDoughnutChartAndTable1
              title={t('language')}
              data={languageData}
              doughnutChartUseElbowFunction={true}
            />
          </Col>
        </Row>
      )}

      {ageData && (
        <Row className="mb-4">
          <Col className="mb-4 px-0">
            <LomavisDoughnutChartAndTable1
              title={t('age')}
              data={ageData}
              barChartEnableSorting={false}
            />
          </Col>
        </Row>
      )}

      {genderData && (
        <Row className="mb-4">
          <Col className="mb-4 px-0">
            <LomavisDoughnutChartAndTable1
              title={t('gender')}
              data={genderData}
            />
          </Col>
        </Row>
      )}

      {seniorityData && (
        <Row className="mb-4">
          <Col className="mb-4 px-0">
            <LomavisDoughnutChartAndTable1
              title={t('seniority')}
              data={seniorityData}
            />
          </Col>
        </Row>
      )}

      {industriesData && (
        <Row className="mb-4">
          <Col className="mb-4 px-0">
            <LomavisDoughnutChartAndTable1
              title={t('industries')}
              data={industriesData}
              doughnutChartUseElbowFunction={true}
            />
          </Col>
        </Row>
      )}

      {functionsData && (
        <Row className="mb-4">
          <Col className="mb-4 px-0">
            <LomavisDoughnutChartAndTable1
              title={t('functions')}
              data={functionsData}
              doughnutChartUseElbowFunction={true}
            />
          </Col>
        </Row>
      )}

      {companySizesData && (
        <Row className="mb-4">
          <Col className="mb-4 px-0">
            <LomavisDoughnutChartAndTable1
              title={t('company_sizes')}
              data={companySizesData}
              barChartEnableSorting={false}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
};
